import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { BASELINE, CAP_TOP, type LetterBox } from '../Ballpit/letterPegs'

export interface LetterTarget {
  left: number
  top: number
  width: number
  height: number
}

interface TitleRowProps {
  text: string
  fontFamily: string
  textColor?: string
  minFontSize?: number
  /**
   * Where each letter (spaces skipped) must stand, in CSS pixels: each glyph's cap box is moved
   * and scaled to fill its target, so the title lands exactly where the 3D letters will.
   */
  letterBoxes?: LetterTarget[] | null
  /** Reports where each letter is on screen whenever the layout changes. */
  onMeasure?: (letters: LetterBox[]) => void
}

/** The face the title stands in for while the 3D letters load: heavy and condensed. */
const WGHT = 700
const WDTH = 75

/**
 * The DOM title: one span per character in a row that fills its container. It lays the letters
 * out, is the heading for assistive tech and crawlers, and reports each letter's box so the 3D
 * title can stand exactly there. It measures only when the layout can have changed (a size,
 * the font arriving, a resize), never per frame.
 */
export const TitleRow: React.FC<TitleRowProps> = ({
  text,
  fontFamily,
  textColor = '#FFFFFF',
  minFontSize = 24,
  letterBoxes = null,
  onMeasure,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const titleRef = useRef<HTMLHeadingElement | null>(null)
  const spansRef = useRef<(HTMLSpanElement | null)[]>([])
  const onMeasureRef = useRef(onMeasure)
  onMeasureRef.current = onMeasure
  /** The base font size still waiting to be fitted to the container, if any. */
  const fitBaseRef = useRef<number | null>(null)
  const [fontSize, setFontSize] = useState(minFontSize)
  /** Bumped when the layout may have moved without the font size changing (a resize, the font arriving). */
  const [layoutTick, setLayoutTick] = useState(0)

  const chars = text.split('')

  // A base size from the container's width; the layout effect below grows it to fit.
  const setSize = useCallback(() => {
    if (!containerRef.current) return
    const containerW = containerRef.current.getBoundingClientRect().width
    const base = Math.max((containerW / (chars.length / 2)) * 0.85, minFontSize)
    fitBaseRef.current = base
    setFontSize(base)
    setLayoutTick((t) => t + 1)
  }, [chars.length, minFontSize])

  // Once the base size is on screen, fit the font so the letters, set a fifth of a cap apart,
  // exactly fill the container: up for a narrow face, down for a wide one (a row that
  // overflows would sit off centre, and the 3D title with it). Measured after the commit,
  // before paint, so the numbers are the real ones. The 3D title that stands in this row is
  // laid out by the same rule, so both come out the same size.
  useLayoutEffect(() => {
    if (fitBaseRef.current !== fontSize || !containerRef.current) return
    fitBaseRef.current = null
    const containerW = containerRef.current.getBoundingClientRect().width
    const letters = spansRef.current.filter(
      (span): span is HTMLSpanElement => !!span && span.textContent!.trim() !== ''
    )
    const lettersW = letters.reduce((sum, span) => sum + span.offsetWidth, 0)
    const cap = fontSize * 0.71
    const natural = lettersW + cap * 0.22 * (letters.length - 1) + cap * 0.5 * (chars.length - letters.length)
    const fit = Math.min(1.6, containerW / Math.max(1, natural))
    if (Math.abs(fit - 1) > 0.02) setFontSize(fontSize * fit)
  }, [fontSize, chars.length])

  // Report the letters' boxes: layout boxes, not painted ones, so a letter moved onto its 3D
  // target still reports where the row sets it, or the row would chase itself.
  useLayoutEffect(() => {
    const title = titleRef.current
    if (!title) return
    const titleRect = title.getBoundingClientRect()
    const letters: LetterBox[] = []
    spansRef.current.forEach((span) => {
      if (!span) return
      letters.push({
        char: span.dataset.char ?? '',
        x: titleRect.left + span.offsetLeft,
        y: titleRect.top + span.offsetTop,
        width: span.offsetWidth,
        height: span.offsetHeight,
        weight: WGHT,
      })
    })
    onMeasureRef.current?.(letters)
  }, [fontSize, layoutTick])

  // Each glyph's cap box is moved and scaled onto its target, about the cap's centre.
  useLayoutEffect(() => {
    const title = titleRef.current
    if (!title) return
    const spans = spansRef.current.filter((span): span is HTMLSpanElement => !!span)
    const glyphs = spans.filter((span) => span.textContent!.trim() !== '')
    if (!letterBoxes || letterBoxes.length !== glyphs.length) {
      spans.forEach((span) => {
        span.style.transform = ''
      })
      return
    }
    const titleRect = title.getBoundingClientRect()
    const capMid = CAP_TOP + (BASELINE - CAP_TOP) / 2
    glyphs.forEach((span, i) => {
      const target = letterBoxes[i]
      const left = titleRect.left + span.offsetLeft
      const width = span.offsetWidth
      const capHeight = span.offsetHeight * (BASELINE - CAP_TOP)
      const capCentreY = titleRect.top + span.offsetTop + span.offsetHeight * capMid
      const dx = target.left + target.width / 2 - (left + width / 2)
      const dy = target.top + target.height / 2 - capCentreY
      span.style.transformOrigin = `50% ${capMid * 100}%`
      span.style.transform = `translate(${dx.toFixed(2)}px, ${dy.toFixed(2)}px) scale(${(target.width / width).toFixed(4)}, ${(target.height / capHeight).toFixed(4)})`
    })
  }, [letterBoxes, fontSize, layoutTick])

  // Layout effect: the right font size is on screen at first paint, no jump from the minimum.
  // The web font arrives after first paint and changes every width: size again then.
  useLayoutEffect(() => {
    setSize()
    window.addEventListener('resize', setSize)
    let live = true
    document.fonts?.ready.then(() => {
      if (live) setSize()
    })
    return () => {
      live = false
      window.removeEventListener('resize', setSize)
    }
  }, [setSize])

  return (
    <div ref={containerRef} className="relative h-full w-full bg-transparent">
      <h1
        ref={titleRef}
        aria-label={text}
        className="text-pressure-title flex justify-between text-center uppercase"
        style={{
          fontFamily,
          fontSize,
          lineHeight: 1,
          position: 'relative',
          margin: 0,
          whiteSpace: 'nowrap',
          userSelect: 'none',
          width: '100%',
          color: textColor,
          fontVariationSettings: `'wght' ${WGHT}, 'wdth' ${WDTH}, 'ital' 0`,
        }}
      >
        {chars.map((char, i) => (
          <span
            key={i}
            ref={(el) => {
              spansRef.current[i] = el
            }}
            data-char={char}
            aria-hidden="true"
            className="inline-block"
            style={{ fontVariationSettings: `'wght' ${WGHT}, 'wdth' ${WDTH}, 'ital' 0` }}
          >
            {char}
          </span>
        ))}
      </h1>
    </div>
  )
}
